/**
 * CustomFieldsSettingsPage — manage fias_leads_custom_field_definitions.
 * Per-entity (contact / company / deal) schemas. The values for each
 * row are stored in the metadata JSONB of that entity (v1 — not
 * surfaced in the list/detail forms yet; field definitions are the
 * schema-only piece).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsCustomFields,
  useCreateFiasLeadsCustomField,
  useDeleteFiasLeadsCustomField,
} from '../hooks/useFiasLeads';
import type { FiasLeadsCustomFieldEntityType, FiasLeadsCustomFieldType } from '@/lib/api';
import { SkeletonBlock } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const ENTITY_TYPES: FiasLeadsCustomFieldEntityType[] = ['contact', 'company', 'deal'];
const FIELD_TYPES: FiasLeadsCustomFieldType[] = [
  'text',
  'number',
  'date',
  'select',
  'multiselect',
  'boolean',
  'url',
  'email',
];

export function CustomFieldsSettingsPage({ holderId }: Props) {
  const [activeEntity, setActiveEntity] = useState<FiasLeadsCustomFieldEntityType>('contact');
  const fieldsQuery = useFiasLeadsCustomFields(holderId, activeEntity);
  const createField = useCreateFiasLeadsCustomField(holderId);
  const deleteField = useDeleteFiasLeadsCustomField(holderId);

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{
    fieldName: string;
    fieldLabel: string;
    fieldType: FiasLeadsCustomFieldType;
    isRequired: boolean;
    optionsCsv: string;
  }>({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    isRequired: false,
    optionsCsv: '',
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.fieldName.trim() || !draft.fieldLabel.trim()) return;
    const options =
      draft.fieldType === 'select' || draft.fieldType === 'multiselect'
        ? draft.optionsCsv
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    await createField.mutateAsync({
      entityType: activeEntity,
      fieldName: draft.fieldName.trim(),
      fieldLabel: draft.fieldLabel.trim(),
      fieldType: draft.fieldType,
      options,
      isRequired: draft.isRequired,
    });
    setDraft({
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      isRequired: false,
      optionsCsv: '',
    });
    setShowCreate(false);
  };

  const fields = fieldsQuery.data ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-4">
      <Link
        to={`${ARCHE_PATH}/settings`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Settings
      </Link>

      <header>
        <h1 className="text-2xl font-semibold">Custom fields</h1>
        <p className="text-sm text-muted-foreground">
          Per-entity custom field schemas. Values stored in entity metadata.
        </p>
      </header>

      <div className="flex gap-1 rounded border border-border bg-card p-1 w-fit">
        {ENTITY_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setActiveEntity(t)}
            className={
              'rounded px-3 py-1 text-sm transition-colors ' +
              (activeEntity === t
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
        >
          <Plus className="h-4 w-4" />
          New field
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="rounded border border-border bg-card p-4 flex flex-col gap-2"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Field name (snake_case) *"
              value={draft.fieldName}
              onChange={(e) => setDraft((d) => ({ ...d, fieldName: e.target.value }))}
              className="rounded border border-border bg-background px-3 py-2 text-sm"
              required
              pattern="[a-z0-9_]+"
              title="Lowercase letters, digits, and underscores only"
            />
            <input
              type="text"
              placeholder="Display label *"
              value={draft.fieldLabel}
              onChange={(e) => setDraft((d) => ({ ...d, fieldLabel: e.target.value }))}
              className="rounded border border-border bg-background px-3 py-2 text-sm"
              required
            />
            <select
              value={draft.fieldType}
              onChange={(e) =>
                setDraft((d) => ({ ...d, fieldType: e.target.value as FiasLeadsCustomFieldType }))
              }
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isRequired}
                onChange={(e) => setDraft((d) => ({ ...d, isRequired: e.target.checked }))}
              />
              Required
            </label>
          </div>
          {(draft.fieldType === 'select' || draft.fieldType === 'multiselect') && (
            <input
              type="text"
              placeholder="Options (comma-separated)"
              value={draft.optionsCsv}
              onChange={(e) => setDraft((d) => ({ ...d, optionsCsv: e.target.value }))}
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            />
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={
                !draft.fieldName.trim() || !draft.fieldLabel.trim() || createField.isPending
              }
              className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createField.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {fieldsQuery.isLoading && <SkeletonBlock lines={4} />}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {fields.map((f) => (
            <li key={f.id} className="p-3 flex items-center gap-3 group">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  {f.field_label}{' '}
                  <code className="text-xs text-muted-foreground">{f.field_name}</code>
                </p>
                <p className="text-xs text-muted-foreground">
                  {f.field_type}
                  {f.is_required ? ' · required' : ''}
                  {Array.isArray(f.options) && f.options.length > 0
                    ? ` · options: ${(f.options as string[]).join(', ')}`
                    : ''}
                </p>
              </div>
              <button
                onClick={() => {
                  if (
                    confirm(
                      `Delete field "${f.field_label}"? Values stored in entity metadata stay.`,
                    )
                  ) {
                    deleteField.mutateAsync(f.id);
                  }
                }}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        {fields.length === 0 && !fieldsQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            No custom fields for {activeEntity}s yet.
          </p>
        )}
      </div>
    </div>
  );
}
